import {
  Button,
  Notification,
  Text,
  Group,
  CloseButton,
  LoadingOverlay,
  rem,
  Select,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { AddressCard } from './Cards/AddressCard';
import { PersonInfoCard } from './Cards/PersonInfoCard';
import { ContactPersonCard } from './Cards/ContactPersonCard';
import { StartCareDateCard } from './Cards/StartCareDateCard';
import { CareRequirementsCard } from './Cards/CareRequirementsCard';
import { dateFormat, dateFormatUI, locale } from '@/index';
import { createFormContext, zodResolver } from '@mantine/form';
import { useNavigate, useParams } from 'react-router-dom';
import useSWR from 'swr';
import { getFetcher, postFetcher } from '@/api/fetchers';
import dayjs from 'dayjs';
import { type DateValue } from '@mantine/dates';
import { modals } from '@mantine/modals'; // Add this line
import { useEffect } from 'react';
import { isEmptyForm } from '@/utils/functions';
import { notifications } from '@mantine/notifications';
import { IconX } from '@tabler/icons-react';
import { type Services } from '@/schemas/API/GET/service/response';
import { type ClientDetailed } from '@/schemas/API/GET/client/response';
import { type ApplicantDetailed } from '@/schemas/API/GET/applicant/response';
import {
  type CreateApplicant,
  createApplicantSchema,
  defaultApplicant,
} from '@/schemas/API/POST/applicant/request';
import { displayError } from '@/utils/error/errorHandler';

export const [
  ApplicantFormProvider,
  useApplicantFormContext,
  useApplicantForm,
] = createFormContext<CreateApplicant>();

export const NewApplicantClientFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { clientId } = useParams<{ clientId: string }>();

  const { data: availableServices } = useSWR<Services>('services', getFetcher);
  const { data: client, error } = useSWR<ClientDetailed>(
    `clients/${clientId}`,
    getFetcher
  );

  const applicantForm = useApplicantForm({
    validate: zodResolver(createApplicantSchema),
    validateInputOnChange: true,
  });

  useEffect(() => {
    if (!client || !availableServices) {
      applicantForm.setInitialValues(defaultApplicant);
      applicantForm.setValues(defaultApplicant);
      applicantForm.resetDirty(defaultApplicant);
      return;
    }

    // Combine fetched client and default values
    const updatedValues = {
      clientId: client.id,
      title: client.title ?? undefined,
      firstName: client.firstName,
      lastName: client.lastName,
      birthdate: client.birthdate,
      sex: client.sex,
      permAddress: client.permAddress,
      servAddress: client.servAddress,
      serviceId: applicantForm.values.serviceId || defaultApplicant.serviceId,
      keyEmp: applicantForm.values.keyEmp || defaultApplicant.keyEmp,
      statusDesc:
        applicantForm.values.statusDesc || defaultApplicant.statusDesc,
      careReq: applicantForm.values.careReq || defaultApplicant.careReq,
      note: applicantForm.values.note ?? defaultApplicant.note,
      prefTime: applicantForm.values.prefTime ?? defaultApplicant.prefTime,
      commDate: applicantForm.values.commDate || defaultApplicant.commDate,
      startDate: applicantForm.values.startDate ?? defaultApplicant.startDate,
      cancelReasons:
        applicantForm.values.cancelReasons || defaultApplicant.cancelReasons,
      contacts: applicantForm.values.contacts || defaultApplicant.contacts,
    };

    applicantForm.setInitialValues(updatedValues);
    applicantForm.setValues(updatedValues);
    applicantForm.resetDirty(updatedValues);
  }, [client, availableServices]);

  const isDirtyExceptClientFields = (): boolean => {
    const dirty = Object.keys(applicantForm.values).some(
      key =>
        ![
          'clientId',
          'title',
          'firstName',
          'lastName',
          'birthdate',
          'sex',
          'permAddress',
          'servAddress',
        ].includes(key) && applicantForm.isDirty(key)
    );

    return dirty;
  };

  const handleCancel = (): void => {
    if (!isDirtyExceptClientFields()) {
      navigate('/applicants');
      return;
    }
    modals.openConfirmModal({
      title: 'Odejít bez uložení?',
      centered: true,
      children: (
        <Text>
          Formulář obsahuje změny, které nebyly uloženy. Určitě chcete formulář
          uzavřít?
        </Text>
      ),
      labels: { confirm: 'Odejít', cancel: 'Zrušit' },
      confirmProps: { color: 'blue' },
      onCancel: () => {},
      onConfirm: () => {
        navigate('/applicants');
      },
    });
  };

  const handleSave = async (): Promise<void> => {
    const validationResult = applicantForm.validate();

    if (validationResult.hasErrors) {
      console.error('Form failed validation:', validationResult.errors);
      notifications.show({
        title: `Zájemce nebylo možné uložit.`,
        message: `Formulář obsahuje chyby.`,
        color: 'red',
        icon: <IconX style={{ width: rem(20), height: rem(20) }} />,
      });
      return;
    }

    try {
      const applicant: ApplicantDetailed = await postFetcher(
        `/applicants`,
        applicantForm.values
      );
      navigate(`/applicants/${applicant.id}`);
    } catch (error) {
      displayError(error);
    }
  };

  if (error) {
    console.error(`Error while fetching client ${clientId}:`, error);

    const errIcon = <IconX style={{ width: rem(20), height: rem(20) }} />;

    return (
      <Notification
        radius="md"
        withBorder
        withCloseButton={false}
        icon={errIcon}
        color="red"
        title="Jejda!"
        styles={{
          root: {
            boxShadow: 'none',
            height: '15%',
            width: '50%',
            marginTop: '20%',
          },
        }}
      >
        Něco se pokazilo
      </Notification>
    );
  }

  if (!isEmptyForm(applicantForm) && client && availableServices) {
    return (
      <ApplicantFormProvider form={applicantForm}>
        <div className="p-8">
          <div className="flex flex-col sm:rounded-card sm:border sm:border-card sm:p-2.5 gap-2.5">
            <Group justify="space-between">
              <Text fw={700} size="xl">
                Formulář zájemce
              </Text>
              <CloseButton onClick={() => handleCancel()} />
            </Group>

            <Group justify="space-between">
              <DateInput
                className="w-full max-w-[235px]"
                variant="filled"
                label="Datum komunikace"
                placeholder="Vyberte datum"
                valueFormat={dateFormatUI}
                locale={locale}
                value={
                  applicantForm.values.commDate === ''
                    ? dayjs().toDate()
                    : dayjs(applicantForm.values.commDate).toDate()
                }
                onChange={(d: DateValue) => {
                  const date: string = dayjs(d).format(dateFormat);
                  applicantForm.setFieldValue('commDate', date);
                }}
                error={applicantForm.getInputProps('commDate').error}
              />
              <Select
                label="Služba"
                onChange={e => {
                  const newValue = parseInt(e ?? '0');
                  applicantForm.setFieldValue('serviceId', newValue);
                }}
                data={availableServices.map(service => ({
                  value: service.id.toString(),
                  label: service.name,
                  disabled: client?.carePlans.some(
                    carePlan => carePlan.serviceId === service.id
                  ),
                }))}
                placeholder="Vyberte službu"
                error={applicantForm.getInputProps('serviceId').error}
              />
            </Group>

            <PersonInfoCard
              title="Zájemce o službu"
              formContext={useApplicantFormContext}
              disabled
            />

            <Group justify="space-between" grow className="flex items-start">
              <AddressCard
                addressType="Trvalá adresa"
                formContext={useApplicantFormContext}
                disabled
              />
              <AddressCard
                addressType="Adresa poskytování"
                formContext={useApplicantFormContext}
                disabled
              />
            </Group>

            <ContactPersonCard
              title="Kontaktující"
              formContext={useApplicantFormContext}
            />

            <StartCareDateCard formContext={useApplicantFormContext} />

            <CareRequirementsCard formContext={useApplicantFormContext} />

            <Group justify="flex-end">
              <Button variant="outline" onClick={handleCancel}>
                Zrušit
              </Button>

              <Button
                className="w-28"
                onClick={() => {
                  void handleSave();
                }}
              >
                Uložit
              </Button>
            </Group>
          </div>
        </div>
      </ApplicantFormProvider>
    );
  }

  return (
    <div className="relative" style={{ height: '100%', width: '100%' }}>
      <LoadingOverlay
        visible
        zIndex={1000}
        overlayProps={{ radius: 'sm', blur: 2 }}
      />
    </div>
  );
};
